import { useCallback, useEffect, useRef, useState } from "react";
import { useIdleTimer } from "react-idle-timer";
import { StreakCheckInfo } from "@lifeat/shared/data-models/user/UserMetrics/UserMetrics";
import * as UserMetricsUtil from "@lifeat/shared/data-models/user/UserMetrics/UserMetricsUtil";
import { TIME_SPENT_DURATION } from "@lifeat/shared/user/TimeSpentUserMetricsConstants";
import * as DateUtil from "@lifeat/shared/util/DateUtil";
import { useUserContext } from "@contexts/UserContext";
import * as AllUserMetricsService from "@user/service/AllUserMetricsService";

const IDLE_TIME = 7200000; // 2 hours

// Note: this hook should only be used in UserMetricsButton.tsx
// To set the interval for time tracking
export function useTimeTrackingMetric() {
    const { user, userData } = useUserContext();

    const [userIsInactive, setUserIsInactive] = useState(false);

    const heartbeatRef = useRef(null);

    const [streakMetricsNode, setStreakMetricsNode] = useState(userData?.metrics?.streakMetrics);

    // Note: this is to handle extracting the streakMetrics object so that the
    // useEffect for setting interval only gets triggered when the contents of streakMetrics changes
    useEffect(() => {
        if (userData?.metrics?.streakMetrics) {
            if (JSON.stringify(userData.metrics.streakMetrics) !== JSON.stringify(streakMetricsNode)) {
                setStreakMetricsNode(userData.metrics.streakMetrics);
            }
        }
    }, [userData?.metrics?.streakMetrics]);

    const updateTimeSpent = useCallback(() => {
        if (userData) {
            const today = new Date();
            const timestamp = today.getTime();
            const isoTimeStamp = DateUtil.getCurrDateIsoTimeStamp();
            const streakCheckInfo: StreakCheckInfo = UserMetricsUtil.canUserIncreaseStreak(
                isoTimeStamp,
                userData?.metrics?.streakMetrics?.lastLoginDateTimestampIsoStr,
            );

            const formattedDate = parseInt(DateUtil.formatDateStringYYYYMMDD(today));

            // update time spent in DB for current day
            AllUserMetricsService.updateTimeSpent({
                timestamp: timestamp,
                date: formattedDate,
                newLoginTimestampISOString: isoTimeStamp,
                streakCheckInfo: streakCheckInfo,
            });
        }
    }, [userData]);

    const handleInactive = () => {
        setUserIsInactive(true);
        clearInterval(heartbeatRef.current); // stop tracking time on inactive
        heartbeatRef.current = null;
    };

    const handleActive = () => {
        setUserIsInactive(false);

        if (!heartbeatRef.current && user && userData) {
            heartbeatRef.current = setInterval(() => {
                updateTimeSpent();
            }, TIME_SPENT_DURATION);
        }
    };

    useIdleTimer({
        timeout: IDLE_TIME,
        onIdle: handleInactive,
        onActive: handleActive,
        startOnMount: true,
    });

    // Set interval for updating time spent
    useEffect(() => {
        if (streakMetricsNode) {
            heartbeatRef.current = setInterval(() => {
                updateTimeSpent();
            }, TIME_SPENT_DURATION);
        }

        return () => {
            clearInterval(heartbeatRef.current);
            heartbeatRef.current = null;
        };
    }, [streakMetricsNode]);

    return userIsInactive;
}
